import React, { useEffect } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { useAuth } from '../../_helper/Auth/AuthProvider'
import { PageNotes } from '../../Components/StudentManagement/PageNotes'

/**
 * StudentPageLayout - A layout wrapper for student-related pages
 *
 * Features:
 * - Shows PageNotes for both instructors and students
 * - Automatically determines the correct studentId based on user role
 * - Handles pageName for PageNotes
 * - Automatically opens/closes accordions based on active sidebar menu
 * - StudentTopMenu is now in the sidebar instead of here
 *
 * @param {string} activeMenu - The active menu item (used for accordion control)
 * @param {string} pageName - The page name for PageNotes
 * @param {React.ReactNode} children - The page content to render
 * @param {boolean} showPageNotes - Whether to show PageNotes (default: true)
 */
const StudentTopMenuLayout = ({ activeMenu, pageName, children, showPageNotes = true }) => {
	const { studentId } = useParams()
	const { user } = useAuth()
	const location = useLocation()

	// Detect if this is being viewed by instructor or student
	const isInstructorView = user?.role === 'Instructor'

	// Use studentId from params if instructor, otherwise use logged-in user's id
	const effectiveStudentId = isInstructorView ? studentId : user?.id

	/**
	 * Map activeMenu values to accordion IDs
	 * @param {string} menuKey - The activeMenu value
	 * @returns {string|null} The accordion ID to open, or null if no mapping exists
	 */
	const getAccordionIdsFromActiveMenu = (menuKey) => {
		if (!menuKey) return []

		const menuToAccordionMap = {
			'Agenda': ['collapseNotes', 'collapseTask'], // Agenda Management (collapseAgenda) is omitted to stay closed
			'Schedule': ['collapseSchedule'],
			'Lessons': ['collapseLessons'],
			'GradeList': ['collapseGrades'],
			'GoalGrades': ['collapseSAT'],
			'BookList': ['collapseBookList'],
			'Activity': ['collapseActivities'],
			'Awards': ['collapseAwards'],
			'Activities/Awards': ['collapseActivitiesAwards'],
			'PersonalQuestions': ['collapsePersonalQuestions'],
			'Resource': ['collapseResourceList'],
			'CollegeList': ['collapseCollegeList'],
			'CollegeList911': ['collapseCollegeList911'],
			'BragSheet': ['collapseBragSheet'],
			'AddInfo': ['collapseAdditionalInfo'],
			'Timeline': ['collapseCurriculum']
		}

		return menuToAccordionMap[menuKey] || []
	}

	/**
	 * Get the accordion ID that should be open based on activeMenu prop or URL path
	 * @returns {string|null} The accordion ID to open, or null if none should be open
	 */
	const getAccordionsToOpen = () => {
		// First, try to get accordion IDs from activeMenu prop
		if (activeMenu) {
			const accordionIds = getAccordionIdsFromActiveMenu(activeMenu)
			if (accordionIds.length > 0) {
				return accordionIds
			}
		}

		// Fallback to URL path-based detection
		const path = location.pathname.toLowerCase()
		
		// Map routes to accordion IDs (order matters - more specific paths first)
		if (path.includes('/agenda')) {
			return ['collapseNotes', 'collapseTask']
		} else if (path.includes('/schedule')) {
			return ['collapseSchedule']
		} else if (path.includes('/lessons') || path.includes('/courses')) {
			return ['collapseLessons']
		} else if (path.includes('/gradeinmonth') || path.includes('/grades') || path.includes('/grade-list')) {
			return ['collapseGrades']
		} else if (path.includes('/goal-questions') || path.includes('/sat')) {
			return ['collapseSAT']
		} else if (path.includes('/booklist') || path.includes('/readings')) {
			return ['collapseBookList']
		} else if (path.includes('/activities')) {
			return ['collapseActivities']
		} else if (path.includes('/awards')) {
			return ['collapseAwards']
		} else if (path.includes('/personal-questions') || path.includes('/career')) {
			return ['collapsePersonalQuestions']
		} else if (path.includes('/resource')) {
			return ['collapseResourceList']
		} else if (path.includes('/college-profile')) {
			return ['collapseCollegeProfile']
		} else if (path.includes('/student-911-college-list') || path.includes('/student911/college-list')) {
			return ['collapseCollegeList911']
		}
		
		return []
	}

	/**
	 * Control accordion state based on activeMenu prop or URL
	 */
	useEffect(() => {
		const accordionsToOpen = getAccordionsToOpen()
		
		// List of all possible accordion IDs
		const allAccordions = [
			'collapseNotes',
			'collapseTask',
			'collapseAgenda',
			'collapseSchedule',
			'collapseLessons',
			'collapseGrades',
			'collapseSAT',
			'collapseBookList',
			'collapseActivities',
			'collapseAwards',
			'collapseActivitiesAwards',
			'collapsePersonalQuestions',
			'collapseResourceList',
			'collapseCollegeProfile',
			'collapseCollegeList',
			'collapseCollegeList911',
			'collapseBragSheet',
			'collapseAdditionalInfo',
			'collapseCurriculum'
		]

		// Function to control accordion state
		const controlAccordion = () => {
			// Close all accordions first
			allAccordions.forEach((accordionId) => {
				const element = document.getElementById(accordionId)
				if (element) {
					// Remove 'show' class to close
					element.classList.remove('show')
					// Update aria-expanded on the button
					const button = document.querySelector(`[data-bs-target="#${accordionId}"]`)
					if (button) {
						button.setAttribute('aria-expanded', 'false')
					}
				}
			})

			// Open the accordions that should be open
			accordionsToOpen.forEach((accordionId) => {
				const element = document.getElementById(accordionId)
				if (element) {
					// Add 'show' class to open
					element.classList.add('show')
					// Update aria-expanded on the button
					const button = document.querySelector(`[data-bs-target="#${accordionId}"]`)
					if (button) {
						button.setAttribute('aria-expanded', 'true')
					}
				}
			})
		}

		// Small delay to ensure DOM is ready
		const timeoutId = setTimeout(controlAccordion, 150)
		
		return () => clearTimeout(timeoutId)
	}, [location.pathname, activeMenu])

	return (
		<>
			{/* StudentTopMenu artık sidebar'da gösteriliyor */}

			{/* Show PageNotes for both instructors and students */}
			{showPageNotes && (
				<PageNotes
					type={isInstructorView ? 'Instructor' : 'Student'}
					pageName={pageName}
					studentId={effectiveStudentId}
					instructorId={isInstructorView ? user?.id : null}
					showScheduleButton={activeMenu !== 'Schedule'}
				/>
			)}

			{/* Render the page content */}
			{children}
		</>
	)
}

export { StudentTopMenuLayout }
